/**
 * Credentials for the vendors **this adapter** constructs.
 *
 * These left `OnboardingConfig` on purpose. They are not "values the flow reads" — nothing
 * in the onboarding flow wants a Datadog client token; only the code that builds a Datadog
 * client does, and that code is now optional and lives here. Keeping them in the core
 * config meant every consumer's config object advertised vendors it must never construct.
 *
 * ## One channel, no env fallbacks
 *
 * Unlike `#modules/runtimeConfig`, nothing here falls back to `process.env.EXPO_PUBLIC_*`.
 * The caller passes values to `createVidaVendors` and that is the only channel.
 *
 * Two reasons. `EXPO_PUBLIC_*` does not inline inside `node_modules`, so a fallback here
 * would work in our app and silently read `undefined` anywhere else — the failure mode
 * that made "never call the getter at module scope" a rule you had to remember. And with
 * a single channel there is no merge order to reason about: what you passed is what runs.
 *
 * The standalone app already reads every variable in `app/_layout.tsx`, where babel does
 * inline them, so nothing is lost.
 */
export type VidaVendorConfig = {
  /** Deploy environment — tags RUM, Logs and PostHog super-properties. */
  vidaEnv?: string;
  posthog?: {
    /** Gates client construction entirely. No client at all when false. */
    enabled?: boolean;
    apiKey?: string;
    host?: string;
  };
  datadog?: {
    /** Gates RUM/Logs init entirely. */
    enabled?: boolean;
    applicationId?: string;
    clientToken?: string;
    sessionReplayEnabled?: boolean;
  };
};

/**
 * Module-scoped because the readers are not all React: `configureVidaPostHog` is a plain
 * function, and the two `DatadogProvider`s read it from a `useMemo` / `useEffect` that
 * cannot take it as a prop without threading it through `OnboardingRoot`, which must not
 * know this adapter exists.
 *
 * Published by `createVidaVendors` before anything can read it — it is the first thing
 * that function does, and the `Provider` it returns has not mounted yet at that point.
 */
let config: VidaVendorConfig = {};

/** Publish the adapter's config. Called by `createVidaVendors`. */
export const setVidaVendorConfig = (next: VidaVendorConfig): void => {
  config = next;
};

/** Test-only reset — the config is process-wide and would otherwise leak between tests. */
export const resetVidaVendorConfig = (): void => {
  config = {};
};

export const vidaVendorConfig = (): VidaVendorConfig => config;
